<template>
  <modal-v2
    ref="modalPrintAlert"
    class="print-alert-modal"
    size="large"
    title="Revise as informações antes de imprimir"
    text-align="left"
    auto-body-height
  >
    <template #body>
      <div>
        <p>
          Verifique se todos os dados estão corretos e lembre-se de conferir as políticas de cada transportadora.<br/>
          Depois de imprimir, cole a etiqueta na caixa do seu envio.
        </p>

        <p
          v-if="!shouldHideExpiredText"
          class="mt-5"
        >
          As etiquetas selecionadas que estão com status de <strong>Expirado</strong> não vão ser impressas.
        </p>
      </div>

      <MUIList
        class="print-alert-modal__list"
        orientation="column"
        striped
      >
        <print-alert-list-item
          v-for="id in getCompaniesIds"
          :key="id"
          :logo="resolveCompanyLogo(id)"
        >
          <div
            v-if="isLatamCargo(id)"
            class="flex flex-col gap-2"
          >
            <div v-if="resolveVisibilityWithoutCollect(id)">
              <b
                v-if="hasMultiplePostageTypes(id)"
                class="block mb-1 font-bold"
              >
                Sem coleta:
              </b>

              <span>Poste sua carga em {{ stringfyCollectAgencies(id) }}.</span>

              <print-alert-dynamic-content :orientations="$options.static.printAlertOrientations[id](0)" />
            </div>

            <div v-if="resolveVisibilityWithCollect(id)">
              <b
                v-if="hasMultiplePostageTypes(id)"
                class="block mb-1 font-bold"
              >
                Com coleta:
              </b>

              <print-alert-dynamic-content :orientations="$options.static.printAlertOrientations[id](1)" />
            </div>
          </div>

          <print-alert-dynamic-content
            v-else
            :orientations="$options.static.printAlertOrientations[id]"
          />
        </print-alert-list-item>
      </MUIList>
    </template>

    <template #footer>
      <p>
        Não quer ver mais esta mensagem?<br/>
        Acesse suas
        <strong class="text-primary">
          <a :href="$sanitizeUrl($options.static.printRulesDashUrl)" target="_blank" rel="noopener noreferrer" class="text-primary">configurações</a>
        </strong>.
      </p>

      <div>
        <button type="button" class="mui-button mui-button--minimal" @click="$emit('cancel-print')">
          Revisar
        </button>

        <button type="button" class="mui-button mui-button--primary" @click="$emit('confirm-print')">
          Confirmar
        </button>
      </div>
    </template>
  </modal-v2>
</template>

<script>
// COMMON COMPONENTS
import MUIList from 'commons/js/components/organisms/MUIList.vue';
import ModalV2 from 'commons/js/components/atoms/ModalV2';

// COMPONENTS
import PrintAlertListItem from './PrintAlertListItem';
import PrintAlertDynamicContent from './PrintAlertDynamicContent';

// CONFIG
import ShippingDefinitions from 'commons/js/ShippingDefinitions';

// HELPERS
import { printAlertOrientations } from './utils/printAlertOrientations';

export default {
  name: 'PrintAlert',

  components: {
    'print-alert-list-item': PrintAlertListItem,
    MUIList,
    ModalV2,
    PrintAlertDynamicContent
  },

  props: {
    printOrders: {
      required: true,
      type: Array
    },

    printSettings: {
      required: true,
      type: Object
    },

    shouldHideExpiredText: Boolean
  },

  data() {
    return {
      orders: {},
      ordersByCollect: {},
      printRules: {}
    }
  },

  computed: {
    getCompaniesIds() {
      return Object.keys(this.orders);
    }
  },

  watch: {
    printOrders() {
      this.filterOrdersByCompany();
      this.filterOrdersByCollect();
      this.formatPrintSettingsByCompanyId();
    }
  },

  created() {
    this.filterOrdersByCompany();
    this.filterOrdersByCollect();
    this.formatPrintSettingsByCompanyId();
  },

  methods: {
    open() {
      this.$refs.modalPrintAlert.open();
    },

    close() {
      this.$refs.modalPrintAlert.close();
    },

    isLatamCargo(id) {
      return ShippingDefinitions.methods.isLATAMCargo(id);
    },

    hasMultiplePostageTypes(companyId) {
      return this.resolveVisibilityWithCollect(companyId) && this.resolveVisibilityWithoutCollect(companyId);
    },

    filterOrdersByCompany() {
      this.orders = {};

      for (const order of this.printOrders) {
        if (order.generated_at) continue;

        if (!this.orders[order.service.company.id]) {
          Vue.set(this.orders, order.service.company.id, []);
        }

        this.orders[order.service.company.id].push(order);
      }
    },

    filterOrdersByCollect() {
      this.ordersByCollect = {};

      for (const order of this.printOrders) {
        if (order.generated_at) continue;

        if (!this.ordersByCollect[order.service.company.id]) {
          Vue.set(this.ordersByCollect, order.service.company.id, {withCollect: [], withoutCollect: []});
        }

        if (order.collect) {
          this.ordersByCollect[order.service.company.id].withCollect.push(order);
          continue;
        }

        this.ordersByCollect[order.service.company.id].withoutCollect.push(order);
      }
    },

    formatPrintSettingsByCompanyId() {
      this.printRules = {
        [ShippingDefinitions.CORREIOS_ID]: this.printSettings.correios_print_alert,
        [ShippingDefinitions.JADLOG_ID]: this.printSettings.jadlog_print_alert,
        [ShippingDefinitions.LATAM_CARGO_ID]: {
          collect: this.printSettings.latam_cargo_collect_print_alert,
          desk: this.printSettings.latam_cargo_desk_print_alert,
        },
        [ShippingDefinitions.AZUL_CARGO_EXPRESS_ID]: this.printSettings.azul_cargo_express_print_alert,
        [ShippingDefinitions.BUSLOG_ID]: this.printSettings.buslog_print_alert,
        [ShippingDefinitions.LOGGI_ID]: this.printSettings.loggi_print_alert,
        [ShippingDefinitions.JET_ID]: this.printSettings.jet_print_alert,
      }
    },

    resolveAlertVisibility(companyID) {
      return this.orders[companyID] && !this.printRules[companyID];
    },

    resolveVisibilityWithCollect(companyID) {
      if (!this.ordersByCollect[companyID]) {
        return;
      }
      return this.ordersByCollect[companyID].withCollect.length > 0 && !this.printRules[companyID].collect;
    },

    resolveVisibilityWithoutCollect(companyID) {
      if (!this.ordersByCollect[companyID]) {
        return;
      }
      return this.ordersByCollect[companyID].withoutCollect.length > 0 && !this.printRules[companyID].desk;
    },

    resolveCompanyLogo(companyId) {
      const [target] = this.orders[companyId];

      return target.service.company.picture;
    },

    stringfyCollectAgencies(companyId) {
      const orders = this.ordersByCollect[companyId].withoutCollect;
      const agencies = orders.map(order => {
        if (order.agency) {
          return order.agency.name;
        }
        return 'SEM PONTO DE POSTAGEM';
      });

      return [...new Set(agencies)].join(', ');
    },
  },

  static: {
    printRulesDashUrl: MEConfig.get('dash.url') + '/configuracoes#regras-das-transportadoras',
    printAlertOrientations: {...printAlertOrientations()}
  }
}
</script>

<style lang="stylus" scoped>
  @import '~commons/stylus/core/_vars'

  .print-alert-modal

    >>> .modal-v2

      &__footer

        @media $md
          justify-content space-between

    &__list
      max-height 278px
      flex-wrap nowrap
      overflow-y auto

      @media $md
        max-height 391px
</style>
